<template>
  <teleport to="body">
    <div
      class="market-template-change-modal"
    >
      <div
        class="market-template-change-modal__dialog"
        @click.stop="() => {}"
        ref="marketTemplateChangeRef"
      >
        <div class="market-template-change-modal__header">
          <div class="market-template-change-modal__title">
            Market template change
          </div>
          <button
            class="market-template-change-modal__close-button"
            @click="close"
          >
            <XIcon />
          </button>
          <div
            class="market-template-change-modal__subtitle"
          >
            {{ subtitle }}
          </div>
        </div>
        <div class="market-template-change-modal__content">
          <div
            class="market-template-change-modal__message"
          >
            Changing templates for this event can have an impact in market offering
          </div>
          <div class="market-template-change-modal__form">
            <div class="market-template-change-modal__form__label">
              Market template
            </div>
            <Dropdown
              class="markets-dropdown"
              :label="sportTemplatesLoading ? 'Market templates loading...' : currentSportTemplate"
              :loading="sportTemplatesLoading"
            >
              <DropdownItem
                v-for="item in sportTemplates"
                :key="item.sportTemplateId"
                @click="selectSportTemplate(item)"
                clickable
              >
                <div class="dropdown-item-content">
                  <RadioInput
                    :checked="item.sportTemplateId === currentTemplateId"
                  />
                  {{ item.name }}
                </div>
                <div
                  class="dropdown-item-icon-wrapper"
                  @click.stop="goToSportTemplate(item.sportTemplateId)"
                >
                  <Icon
                    class="dropdown-item__icon"
                    name="external"
                  />
                </div>
              </DropdownItem>
            </Dropdown>
          </div>
        </div>
        <div
          class="market-template-change-modal__footer"
        >
          <Button
            variant="secondary"
            @click="close"
          >
            Dismiss
          </Button>
          <Button
            variant="primary"
            @click="confirm"
            :loading="isConfirming"
            :disabled="!hasTemplateChanged"
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import {
  computed, onMounted, ref,
} from 'vue';
import { useStore } from 'vuex';
import { onClickOutside } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { XIcon } from '@/components/svg';
import Button from '@/components/common/Button';
import * as api from '@/services/api';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';
import RadioInput from '@/components/common/RadioInput';
import Icon from '@/components/common/Icon';

export default {
  components: {
    XIcon,
    Button,
    Dropdown,
    DropdownItem,
    RadioInput,
    Icon,
  },
  props: {},
  emits: ['close'],
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const event = computed(() => store.getters.event);
    const selectedTemplate = ref(null);
    const isConfirming = ref(false);

    const currentTemplateId = computed(() => selectedTemplate.value?.sportTemplateId || event.value.sportTemplate.id);
    const hasTemplateChanged = computed(() => selectedTemplate.value?.sportTemplateId && selectedTemplate.value?.sportTemplateId !== event.value.sportTemplate.id);

    const currentSportTemplate = computed(() => selectedTemplate.value?.name || event.value.sportTemplate.name);

    const close = () => {
      emit('close');
    };

    const subtitle = computed(() => {
      if (!event.value.isUsaView) return event.value.name;
      return event.value.name.split(' v ').reverse().join(' @ ');
    });

    const marketTemplateChangeRef = ref(null);

    onClickOutside(marketTemplateChangeRef, () => {
      close();
    });

    const confirm = async () => {
      if (!selectedTemplate.value) {
        close(true);
        return;
      }

      try {
        isConfirming.value = true;
        await api.updateSportTemplateForEvent({
          eventId: event.value.id,
          sportTemplateId: selectedTemplate.value.sportTemplateId,
        });

        store.dispatch('addNotification', {
          message: 'Sport template has been updated successfully',
          type: 'success',
          duration: 5000,
        });

        close(true);
      } catch (error) {
        store.dispatch('addNotification', {
          message: 'Template update failed. Try again later.',
          type: 'error',
          duration: 5000,
        });
      } finally {
        isConfirming.value = false;
        store.dispatch('loadEvent', event.value.id);
      }
    };

    const sportTemplates = ref([]);
    const sportTemplatesLoading = ref(true);
    const loadSportTemplates = () => {
      sportTemplates.value = [];
      const variables = {
        filter: {
          sport: {
            limbo: {
              equalTo: false,
            },
          },
          sportId: {
            equalTo: event.value.sportId,
          },
        },
      };
      api.findSportTemplates(variables)
        .then((sportTemplatesResponse) => {
          sportTemplates.value = sportTemplatesResponse;
        })
        .finally(() => {
          sportTemplatesLoading.value = false;
        });
    };

    const goToSportTemplate = (sportTemplateId) => {
      const { href } = router.resolve({
        name: 'sport-template',
        params: {
          id: sportTemplateId,
        },
      });
      window.open(href, '_blank');
    };

    const selectSportTemplate = ({ sportTemplateId, name }) => {
      selectedTemplate.value = { sportTemplateId, name };
    };

    onMounted(() => {
      loadSportTemplates();
    });

    return {
      confirm,
      close,
      marketTemplateChangeRef,
      subtitle,
      sportTemplates,
      sportTemplatesLoading,
      currentSportTemplate,
      goToSportTemplate,
      selectSportTemplate,
      isConfirming,
      hasTemplateChanged,
      currentTemplateId,
      selectedTemplate,
    };
  },
};
</script>

<style lang="scss" scoped>
.market-template-change-modal {
  $modal: &;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9000;
  background-color: rgba(0, 0, 0, 0.25);

  &__dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border: 1px solid #F0F0F0;
    border-bottom: 0;
    border-radius: 4px;
  }

  &__header {
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 33px;
    grid-template-rows: 1fr auto;
    row-gap: 8px;
    padding: 1rem;
  }

  &__title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  &__subtitle {
    font-family: 'Rubik', sans-serif;
    font-size: 12px;
    line-height: 14px;
    color: #A9A9A9;
  }

  &__close-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    padding: 3px;
    border-radius: 4px;
    background-color: #fff;
    stroke: #191414;

    &:hover { background-color: #FAFAFA; }
    &:active { background-color: #F0F0F0; }
  }

  &__content {
    width: 360px;

    .markets-dropdown {
      position: relative;
      z-index: 1000;

      .dropdown-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 8px;

        &-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        &-icon-wrapper {
          padding: 4px;
          border-radius: 4px;
          cursor: pointer;

          &:hover {
            background-color: #F0F0F0;
          }
        }

        &__icon {
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          stroke: #000;
          display: block;
        }
      }

      .dropdown-menu .dropdown-item {
        gap: 8px;
      }

      .no-dropdown-items-message {
        padding: 16px 8px;
        font-size: 12px;
        color: $gray800;
        text-align: center;
      }
    }
  }

  &__message {
    border-top: 1px solid #F0F0F0;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0.5rem 1rem;
    max-height: 300px;

    &-title {
      font-family: 'Rubik', sans-serif;
      font-weight: 600;
      font-size: 10px;
      line-height: 12px;
      text-transform: uppercase;
      color: #A9A9A9;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &-input {
      display: flex;
      align-items: center;
      width: 44px;
      height: 33px;
      padding: 0 8px;
      gap: 8px;
      background: #FFFFFF;
      border: 1px solid #DDDDDD;
      border-radius: 4px;
      text-align: center;
    }

    &__label {
      font-size: 10px;
      font-weight: 600;
      color: $gray800;
      text-transform: uppercase;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 1rem;
    border-top: 1px solid #F0F0F0;
  }
}
</style>
